// The spike's boot shell and game flow (gates A1.1 and A1.2), composed from @rpg/boot. Everything here is the game's
// values: the steps and their timeouts, the strings, the tier table, the module URL patterns, the flow's states.
//
// The flow: boot (loading screen; Rapier, Recast, the world, shader warm-up) → title (its Start press is the first
// gesture: it unlocks audio, takes pointer lock on a desktop and goes full-screen on a phone) → loading → play;
// pause is pushed over play (the tab hidden, pointer lock lost, or the handle); game over goes back to the title.
// The runtime is paused in every state but play (its loop keeps drawing, so the title sits over the live scene).
import * as THREE from "three";
import {
  createBootShell,
  createFlow,
  enterFullscreen,
  onVisibility,
  requestPointerLock,
  runBoot,
  unlockAudio,
  type BootReport,
  type FlowEvent,
} from "@rpg/boot";
import { captureErrors, createHandle, installHandle } from "@rpg/harness";
import type { Rapier } from "@rpg/collision";
import type { Runtime } from "@rpg/runtime";
import type { VfxSystem } from "@rpg/vfx";
import { bootFaultGroup, bootFaults } from "./faults";
import type { FaultGroup } from "../faults";
import { t } from "./strings";

/** The flow's states. */
export type FlowId = "boot" | "title" | "loading" | "play" | "pause" | "gameover";

/** The game's tier per device class (the tier names are @rpg/look's). */
export const TIERS = { phone: "low", desktop: "high" } as const;

/** URL patterns of each engine module's files: dev serves `.vite/deps/<package>.js`, the build names chunks (vite.config.ts). */
export const MODULE_FILES = { rapier: /rapier/i, recast: /recast/i, quarks: /quarks/i } as const;

/** Step timeouts, ms: generous for a phone on a slow network, short enough that a hang shows. */
const MODULE_TIMEOUT_MS = 20_000;

/** What the game hands the shell once its scene exists. */
export interface BootGame {
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  /** What each flow state shows (the @rpg/ui screens, src/ui/). */
  screens: FlowScreens;
  /** Whether a screen is open over play (a conversation's choices, the inventory): @rpg/ui releases pointer lock for
   *  it, and that release is not the player leaving. Default none. */
  screenOpen?(): boolean;
}

/**
 * The screens the flow drives, one call per state it enters (gate A4.2 moved them onto @rpg/ui). Each call replaces
 * what the UI shows; the actions are the player's ways out of that state.
 */
export interface FlowScreens {
  /** The title. `start` is the first gesture (unlocks, then loading → play): call it inside the press's event. */
  title(a: { start(): void }): void;
  /** The mid-game loader (only shown past 400 ms). */
  loading(): void;
  /** Play: no screen. `from` is the state play came from (loading = a zone change). */
  play(o: { from: FlowId | null }): void;
  pause(a: { resume(): void; toTitle(): void }): void;
  gameover(a: { toTitle(): void }): void;
}

/** The handle's `flow` key: drive and read the flow the way a player would. */
export interface FlowHandle {
  state(): FlowId | null;
  stack(): FlowId[];
  history(): FlowEvent<FlowId>[];
  /** At the title: what the first gesture does (unlocks, then loading → play). */
  start(): void;
  pause(): void;
  resume(): void;
  gameOver(): void;
  toTitle(): void;
  /** What the first gesture unlocked (live checks: pointer lock, full-screen and audio need a real device). */
  unlocks(): { gestures: number; audio: string; pointerLock: boolean; fullscreen: boolean };
  /** Whether the runtime is paused now. */
  paused(): boolean;
  /** Listen to every transition; returns the unsubscribe. */
  on(fn: (e: FlowEvent<FlowId>) => void): () => void;
}

/** The spike's boot. */
export interface SpikeBoot {
  /** Page errors since the page started (pass to the handle so boot-time errors count). */
  errors: string[];
  /** Resolves with Rapier when the world step starts, after the Rapier and Recast steps (stays pending if boot fails before). */
  modules: Promise<{ RAPIER: Rapier }>;
  /** The game's scene exists: the world step finishes and the shell moves on. */
  ready(game: BootGame): void;
  /** Every boot step finished. */
  booted(): boolean;
  /** A boot exception outside the steps: show the fail screen. */
  fail(e: unknown): void;
  /** The boot report for `__rpg.boot()`. */
  report(): BootReport & {
    rapierMs: number | null;
    recastMs: number | null;
    flow: FlowId | null;
    unlocks: ReturnType<FlowHandle["unlocks"]>;
    faults: string[];
    /** The smoke scene's emitters, under `?vfx=smoke`. */
    vfx: ReturnType<VfxSystem["counts"]> | null;
  };
  flow: FlowHandle;
  /** The boot faults as a group of the spike's joined faults record (src/faults.ts). */
  faultGroup: FaultGroup;
  /** The game's one AudioContext, created on the first gesture (null before it); phase D2's audio attaches to it. */
  audioContext(): AudioContext | null;
  /** Hear the context's creation (called at once when it already exists). */
  onAudioContext(fn: (ctx: AudioContext) => void): void;
}

/** Start the boot: the loading screen shows at once and the steps begin. Call once, first thing. */
export function startBoot(): SpikeBoot {
  // the harness's error capture, from the first line, in dev builds only (decision 0006: production never reaches it)
  const errors = import.meta.env.DEV ? captureErrors() : null;
  const params = new URLSearchParams(location.search);
  const faults = bootFaults(location.search);
  const vfxSmoke = params.get("vfx") === "smoke";

  let resolveModules!: (m: { RAPIER: Rapier }) => void;
  const modules = new Promise<{ RAPIER: Rapier }>((r) => (resolveModules = r));
  let resolveGame!: (g: BootGame) => void;
  const gameReady = new Promise<BootGame>((r) => (resolveGame = r));
  let game: BootGame | null = null;
  let booted = false;
  let vfx: VfxSystem | null = null;

  const shell = createBootShell({
    text: {
      loadingTitle: t("boot.loadingTitle"),
      failTitle: t("boot.fail.title"),
      noWebGL2: t("boot.fail.noWebGL2"),
      bootError: t("boot.fail.error"),
      details: t("boot.fail.details"),
      reload: t("boot.fail.reload"),
    },
    tiers: TIERS,
    tierOverride: params.get("quality"),
    modules: MODULE_FILES,
    faults: { timerProgress: faults.has("timer-progress"), noFailScreen: faults.has("no-fail-screen") },
    steps: [
      { id: "rapier", caption: t("boot.step.rapier"), timeoutMs: MODULE_TIMEOUT_MS, run: async () => (await import("@rpg/collision")).initCollision() },
      {
        id: "recast",
        caption: t("boot.step.recast"),
        timeoutMs: MODULE_TIMEOUT_MS,
        run: async () => {
          const nav = await import("@rpg/nav");
          if (faults.has("recast-hang")) nav.NAV_FAULTS.initNeverResolves = true;
          await nav.initNav();
        },
      },
      {
        id: "world",
        caption: t("boot.step.world"),
        timeoutMs: 30_000,
        run: async (c) => {
          if (faults.has("boot-throw")) throw new Error("planted boot exception (fault boot-throw)");
          // hand the modules to the game here, so its scene build counts as this step's time
          resolveModules({ RAPIER: c.results.rapier as Rapier });
          return gameReady;
        },
      },
      ...(vfxSmoke
        ? [{ id: "vfx", caption: t("boot.step.vfx"), timeoutMs: MODULE_TIMEOUT_MS, run: async () => (vfx = (await import("./vfx-smoke")).mountVfxSmoke(game!)) }]
        : []),
      {
        id: "warm",
        caption: t("boot.step.warm"),
        timeoutMs: 30_000,
        run: async () => {
          const g = game!;
          await g.renderer.compileAsync(g.scene, g.camera);
          g.renderer.render(g.scene, g.camera); // the first frame through the real path
        },
      },
    ],
  });
  document.getElementById("gv-preload")?.remove(); // the inline CSS loader (index.html) hands over to the shell's screen
  const desktop = shell.platform.kind === "desktop";

  // the first gesture's unlocks; pointer lock, full-screen and audio are live checks (a real device and a real press)
  const unlocks = { gestures: 0, audio: "none", pointerLock: false, fullscreen: false };
  let audio: AudioContext | null = null;
  const audioListeners: ((ctx: AudioContext) => void)[] = [];
  const gesture = (): void => {
    unlocks.gestures++;
    try {
      if (!audio) {
        audio = new AudioContext();
        for (const fn of audioListeners) fn(audio);
      }
      void unlockAudio(audio).then(() => (unlocks.audio = audio!.state));
    } catch {
      unlocks.audio = "unavailable";
    }
    if (desktop && game) void requestPointerLock(game.renderer.domElement).then((ok) => (unlocks.pointerLock = ok));
    if (!desktop) void enterFullscreen(document.documentElement, { landscape: true }).then((ok) => (unlocks.fullscreen = ok));
  };

  const setPaused = (on: boolean): void => {
    if (game) game.runtime.paused = on;
  };
  let loadingTimer: ReturnType<typeof setTimeout> | null = null;
  const clear = (): void => {
    if (loadingTimer) clearTimeout(loadingTimer);
    loadingTimer = null;
  };
  /** Start from the title: the first gesture's unlocks, then loading → play. */
  const start = (): void => {
    if (!flow.is("title")) return;
    gesture();
    flow.replace("loading");
  };
  const resume = (): void => {
    if (desktop && game) void requestPointerLock(game.renderer.domElement).then((ok) => (unlocks.pointerLock = ok));
    if (flow.is("pause")) flow.pop();
  };
  const toTitle = (): void => flow.reset("title");

  const flow = createFlow<FlowId>({
    states: {
      boot: {
        enter: () =>
          void shell.run().then(
            () => {
              booted = true;
              flow.replace("title");
              setTimeout(
                () =>
                  void shell
                    .measure()
                    .then(() => params.has("bootreport") && showReport())
                    .catch((e) => console.warn("[boot] module sizes", e)),
                0,
              );
            },
            () => undefined, // the shell has shown the fail screen and logged the error
          ),
        exit: () => shell.screen.hide(),
      },
      title: {
        enter: () => {
          setPaused(true);
          game!.screens.title({ start });
        },
        exit: clear,
      },
      loading: {
        enter: () => {
          // a mid-game loader only past 400 ms (boot shell suggestion)
          loadingTimer = setTimeout(() => game!.screens.loading(), 400);
          // the zone's own load steps go here as the world grows; today the valley is already built at boot
          runBoot([{ id: "zone", caption: t("loading.step.zone"), run: () => undefined }]).done.then(
            () => flow.is("loading") && flow.replace("play"),
            (e) => shell.fail(e),
          );
        },
        exit: clear,
      },
      play: {
        enter: () => {
          setPaused(false);
          // the hook runs before the transition is recorded: the last event's target is the state play replaced
          game!.screens.play({ from: flow.history.at(-1)?.to ?? null });
        },
        exit: () => setPaused(true),
        cover: () => setPaused(true),
        uncover: (from) => {
          setPaused(false);
          game!.screens.play({ from });
        },
      },
      pause: {
        enter: () => game!.screens.pause({ resume, toTitle }),
      },
      gameover: {
        enter: () => {
          setPaused(true);
          game!.screens.gameover({ toTitle });
        },
      },
    },
    allowed: { "": ["boot"], boot: ["title"], title: ["loading"], loading: ["play"], play: ["pause", "gameover"], pause: [], gameover: ["title"] },
  });

  // pause when the tab is hidden, or when pointer lock is lost on a desktop (Esc); resuming takes a press. A screen
  // opened over play releases the lock itself (@rpg/ui, so its buttons take clicks): that release is not a pause
  onVisibility((hidden) => hidden && flow.is("play") && flow.push("pause"));
  document.addEventListener("pointerlockchange", () => {
    if (!document.pointerLockElement && flow.is("play") && !game?.screenOpen?.()) flow.push("pause");
  });

  flow.push("boot");

  const flowHandle: FlowHandle = {
    state: () => flow.top,
    stack: () => flow.stack,
    history: () => flow.history.map((e) => ({ ...e, stack: [...e.stack] })),
    start,
    pause: () => void (flow.is("play") && flow.push("pause")),
    resume: () => void (flow.is("pause") && flow.pop()),
    gameOver: () => void (flow.is("play") && flow.replace("gameover")),
    toTitle,
    unlocks: () => ({ ...unlocks }),
    paused: () => game?.runtime.paused ?? true,
    on: (fn) => flow.on(fn),
  };

  const report: SpikeBoot["report"] = () => {
    const r = shell.report();
    return { ...r, rapierMs: r.modules.rapier?.initMs ?? null, recastMs: r.modules.recast?.initMs ?? null, flow: flow.top, unlocks: { ...unlocks }, faults: [...faults], vfx: vfx?.counts() ?? null };
  };

  // `?bootreport`: the boot report on screen, for the live check on a phone with no dev tools (a dev readout, not a
  // player-facing screen, so its text is not in strings.ts)
  const showReport = (): void => {
    const r = report();
    const pre = document.createElement("pre");
    pre.dataset.screen = "bootreport";
    const mod = (k: string): string => {
      const m = r.modules[k];
      return m?.files.length ? `${k.padEnd(7)} ${String(m.initMs ?? "-").padStart(7)} ms ${((m.gzipBytes ?? 0) / 1000).toFixed(0).padStart(6)} kB gz` : "";
    };
    pre.textContent = [
      `${r.platform.kind}, tier ${r.tier} (${r.tierSource}), ${r.platform.viewport.join("×")} @${r.platform.devicePixelRatio}`,
      ...r.steps.map((s) => `${s.id.padEnd(7)} ${s.ms.toFixed(0).padStart(7)} ms`),
      ...Object.keys(r.modules).map(mod).filter(Boolean),
      `boot ${r.totalMs?.toFixed(0)} ms; first frame ${r.firstFrameAtMs?.toFixed(0)} ms after navigation`,
    ].join("\n");
    Object.assign(pre.style, { position: "fixed", left: "8px", bottom: "8px", margin: "0", padding: "6px 8px", background: "rgba(0,0,0,0.6)", color: "#e9e4d6", font: "11px/1.4 ui-monospace, monospace", zIndex: "950", pointerEvents: "none" });
    document.body.appendChild(pre);
  };

  // A boot-time handle, so a spec can read the boot report and see a failed boot (the game's own handle replaces it
  // once the scene exists, carrying the same `errors`, `boot` and `flow`). Dev builds only (decision 0006).
  if (import.meta.env.DEV && errors) installHandle("__rpg", createHandle({ ready: false, state: () => ({ flow: flow.top }), errors }, { boot: report, flow: flowHandle }));

  return {
    errors: errors?.errors ?? [],
    modules,
    ready(g) {
      game = g;
      g.runtime.paused = !flow.is("play");
      // after the game's own render handler; markFirstFrame keeps only the first call (no removal mid-iteration)
      g.runtime.on("render", () => shell.markFirstFrame(), { name: "boot:first-frame", order: 1000 });
      resolveGame(g);
    },
    booted: () => booted,
    audioContext: () => audio,
    onAudioContext: (fn) => {
      audioListeners.push(fn);
      if (audio) fn(audio);
    },
    fail: (e) => shell.fail(e),
    report,
    flow: flowHandle,
    faultGroup: bootFaultGroup(faults),
  };
}
